export function sum(values: readonly number[]): number {
  let total = 0
  for (const v of values) total += v
  return total
}

export function mean(values: readonly number[]): number {
  if (values.length === 0) return 0
  return sum(values) / values.length
}

export function variance(values: readonly number[], sample = true): number {
  const n = values.length
  if (n < 2) return 0
  const m = mean(values)
  let acc = 0
  for (const v of values) acc += (v - m) ** 2
  return acc / (sample ? n - 1 : n)
}

export function stdDev(values: readonly number[], sample = true): number {
  return Math.sqrt(variance(values, sample))
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

export function quantile(values: readonly number[], q: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const pos = clamp(q, 0, 1) * (sorted.length - 1)
  const lo = Math.floor(pos)
  const hi = Math.ceil(pos)
  if (lo === hi) return sorted[lo]
  const frac = pos - lo
  return sorted[lo] * (1 - frac) + sorted[hi] * frac
}

export function min(values: readonly number[]): number {
  if (values.length === 0) return 0
  let m = values[0]
  for (const v of values) if (v < m) m = v
  return m
}

export function max(values: readonly number[]): number {
  if (values.length === 0) return 0
  let m = values[0]
  for (const v of values) if (v > m) m = v
  return m
}

export function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value))
}

export function round(value: number, decimals = 2): number {
  const f = 10 ** decimals
  return Math.round((value + Number.EPSILON) * f) / f
}

export function pctChange(from: number, to: number): number {
  if (from === 0) return 0
  return (to - from) / Math.abs(from)
}

export function covariance(a: readonly number[], b: readonly number[], sample = true): number {
  const n = Math.min(a.length, b.length)
  if (n < 2) return 0
  const ma = mean(a.slice(0, n))
  const mb = mean(b.slice(0, n))
  let acc = 0
  for (let i = 0; i < n; i++) acc += (a[i] - ma) * (b[i] - mb)
  return acc / (sample ? n - 1 : n)
}

export function pearson(a: readonly number[], b: readonly number[]): number {
  const sa = stdDev(a)
  const sb = stdDev(b)
  if (sa === 0 || sb === 0) return 0
  return clamp(covariance(a, b) / (sa * sb), -1, 1)
}

export function logReturns(prices: readonly number[]): number[] {
  const out: number[] = []
  for (let i = 1; i < prices.length; i++) {
    if (prices[i - 1] > 0 && prices[i] > 0) out.push(Math.log(prices[i] / prices[i - 1]))
  }
  return out
}

export function simpleReturns(prices: readonly number[]): number[] {
  const out: number[] = []
  for (let i = 1; i < prices.length; i++) out.push(pctChange(prices[i - 1], prices[i]))
  return out
}

export function zScore(value: number, values: readonly number[]): number {
  const s = stdDev(values)
  if (s === 0) return 0
  return (value - mean(values)) / s
}

export function normalCdf(x: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(x))
  const d = 0.3989422804014327 * Math.exp((-x * x) / 2)
  const p =
    d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  return x > 0 ? 1 - p : p
}
